import { useState } from 'react';
import Box        from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  GhostCard, PrefOverlay, AddPhotoDialog, useRankSync, moveInList,
} from './ShortlistShared3';
import { apiFetch } from '../../../api/apiClient';
import { useToast } from '../../../shared/components/status';

import {
  GREEN, BORDER, MUTED, MUTED_FAINT,
  labelMono, bodyFont, PAGE_PX,
} from '../../../theme/tokens2';

// ─── PhotoCard ────────────────────────────────────────────────────────────────

// Builds the match-count copy, dropping either noun when its count is zero
// and avoiding "1 materials"/"1 furniture pieces" pluralization.
// Returns JSX (not a plain string) so the counts can be bolded + underlined inline.
function buildMatchLabel(materialCount = 0, furnitureCount = 0) {
  const countSx = { fontWeight: 800, fontSize: '10px', textDecoration: 'underline' };

  const parts = [];
  if (materialCount > 0) {
    parts.push(
      <span key="materials">
        <Box component="span" sx={countSx}>{materialCount}</Box> material{materialCount === 1 ? '' : 's'}
      </span>
    );
  }
  if (furnitureCount > 0) {
    parts.push(
      <span key="furniture">
        <Box component="span" sx={countSx}>{furnitureCount}</Box> furniture piece{furnitureCount === 1 ? '' : 's'}
      </span>
    );
  }
  if (!parts.length) return null;

  return (
    <>
      Explore lookalikes for{' '}
      {parts.map((part, i) => (
        <span key={i}>
          {i > 0 && ' and '}
          {part}
        </span>
      ))}
    </>
  );
}

// Derives hasMatches directly from the underlying fields (not from the label
// text), so copy changes downstream can never silently affect border color.
function getMatchInfo(item) {
  if (!item.sourcingcompleted) {
    return { hasMatches: false, label: 'Sourcing in progress…' };
  }
  const hasMatches = (item.material_regions > 0) || (item.furniture_regions > 0);
  return {
    hasMatches,
    label: hasMatches
      ? buildMatchLabel(item.material_regions, item.furniture_regions)
      : 'No close matches found.',
  };
}

function PhotoCard({ item, idx, arrLen, selectedCategory, onOpenLightbox, onMoveUp, onMoveDown, onRemove }) {
  const rank = String(idx + 1).padStart(2, '0');
  const [cardHovered, setCardHovered] = useState(false);

  const { hasMatches, label } = getMatchInfo(item);

  return (
    <Box
      onMouseEnter={() => setCardHovered(true)}
      onMouseLeave={() => setCardHovered(false)}
      sx={{
        position: 'relative',
        border: hasMatches ? `2px solid ${GREEN}` : `0.5px solid ${BORDER}`,
        borderRadius: '3px',
        overflow: 'visible',
        background: '#fff',
      }}>
      {/* The photo itself is the button that opens the Lightbox, so keyboard
          users can reach it with Tab and open it with Enter. */}
      <Box
        component="button"
        type="button"
        onClick={() => onOpenLightbox(idx)}
        aria-label={`Open ${selectedCategory} photo ${rank}`}
        sx={{
          display: 'block', width: '100%', p: 0, m: 0, border: 'none', background: 'none',
          cursor: 'pointer', textAlign: 'left',
          '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '2px' },
        }}
      >
        <Box sx={{
          position: 'relative',
          m: 0.2,
          borderRadius: '2px',
          overflow: 'hidden',
        }}>
          <Box sx={{ position: 'relative', width: '100%', aspectRatio: '4/5' }}>
            <Box
              component="img"
              src={item.imagesrc}
              alt={`${selectedCategory} inspiration photo ${rank}`}
              loading="lazy"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />

            {/* Rank badge — bottom-left, dark glass */}
            <Box sx={{
              position: 'absolute', bottom: 6, left: 6,
              ...labelMono, fontSize: '8px',
              background: 'rgba(18,18,18,0.62)',
              color: 'rgba(255,255,255,0.85)',
              px: '5px', py: '2px',
            }}>
              {rank}
            </Box>

            {/* Savvy pick badge — bottom-right */}
            {item.savvyproposal && (
              <Box sx={{
                position: 'absolute', bottom: 6, right: 6,
                ...labelMono, fontSize: '7px',
                background: 'rgba(255,255,255,0.92)', color: GREEN,
                border: `1px solid ${GREEN}`, px: '4px', py: '2px',
              }}>
                Savvy pick
              </Box>
            )}
          </Box>
        </Box>

        {/* Match-info label — always shown, copy/color driven by hasMatches */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '0.5px', px: '8px', py: '5px' }}>
          <Typography component="span" sx={{
            ...labelMono, fontSize: '8px', fontWeight: hasMatches ? 700 : 400,
            color: hasMatches ? GREEN : MUTED_FAINT,
            lineHeight: 1.4,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            flex: 1, minWidth: 0,
          }}>
            {label}
          </Typography>
          {hasMatches && (
            <Typography component="span" aria-hidden sx={{ ...labelMono, fontSize: '8px', fontWeight: 700, color: GREEN, flexShrink: 0 }}>
              →
            </Typography>
          )}
        </Box>
      </Box>

      {/* Preference overlay — top-right. Sits outside the photo button, so
          its own buttons aren't nested inside another button. */}
      <PrefOverlay
        onMoveUp={onMoveUp} onMoveDown={onMoveDown} onRemove={onRemove}
        isFirst={idx === 0} isLast={idx === arrLen - 1}
        cardHovered={cardHovered}
        itemLabel={`photo ${rank}`}
      />
    </Box>
  );
}

// ─── Scenes ───────────────────────────────────────────────────────────────────
// Props:
//   items            — photo items for the current room (sorted, provided by parent)
//   selectedCategory — current room name, used in upload dialog
//   phoneNumber      — for the uploaded file's name
//   onOpenLightbox   — (index) => void — called when user taps a photo
//   onItemsChanged   — (updatedItems) => void — called after ranking/delete changes
//   onRefetch        — () => void — called after upload so parent can re-fetch all scenes
export default function Scenes({
  items = [],
  selectedCategory,
  phoneNumber,
  onOpenLightbox,
  onItemsChanged,
  onRefetch,
}) {
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const toast = useToast();

  // Saves the new order only after the customer moves a photo.
  const queueRankSave = useRankSync('/api/scenes/bulk');

  const sortedItems = [...items].sort((a, b) => a.ranking - b.ranking);

  const move = (idx, direction) => {
    const next = moveInList(sortedItems, idx, direction);
    if (!next) return;
    onItemsChanged(next);
    queueRankSave(next);
  };

  const removeItem = async (id) => {
    try {
      await apiFetch(`/api/scenes/${id}`, { method: 'DELETE' });
      const next = sortedItems
        .filter((it) => it.id !== id)
        .map((it, i) => ({ ...it, ranking: i + 1 }));
      onItemsChanged(next);
      if (next.length) queueRankSave(next);
      toast.success('Photo removed');
    } catch (err) {
      toast.error("We couldn't remove this photo. Please try again.");
    }
  };

  const handleFileUpload = async (url) => {
    if (!url) return; // the person removed the file inside the dialog
    try {
      await apiFetch('/api/scenes', {
        method: 'POST',
        body: { cat_room: selectedCategory, imagesrc: url },
      });
      setOpenUploadModal(false);
      onRefetch?.();
    } catch (err) {
      toast.error("Your photo uploaded, but we couldn't add it to this room. Please try again.");
    }
  };

  return (
    <Box sx={{ py: '20px', px: PAGE_PX, flex: 1 }}>

      {/* Grid header */}
      <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED, mb: '14px' }}>
        {sortedItems.length > 0 ? `${sortedItems.length} photo${sortedItems.length !== 1 ? 's' : ''}` : ''}
      </Typography>

      {sortedItems.length === 0 ? (
        /* ── Empty state — sole affordance when there's nothing yet ── */
        <Box sx={{
          mt: 2, py: 6, px: 3,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
          background: '#fff',
          border: `0.5px solid ${BORDER}`, borderRadius: '3px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED_FAINT }}>
            No photos yet
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: MUTED, textAlign: 'center', ...bodyFont }}>
            Upload inspiration photos for {selectedCategory} and we'll help you find matching materials and furniture items.
          </Typography>

          <Box
            component="button"
            type="button"
            onClick={() => setOpenUploadModal(true)}
            sx={{
              ...labelMono, fontSize: '9px', color: GREEN,
              background: 'none', border: `0.5px solid ${GREEN}`,
              cursor: 'pointer', px: '14px', py: '7px',
              '&:hover': { background: 'rgba(33,131,51,0.06)' },
            }}
          >
            + Add first photo
          </Box>
        </Box>
      ) : (
        /* ── Photo grid — GhostCard only appears once there's something to append to ── */
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '8px',
        }}>
          {sortedItems.map((item, idx) => (
            <PhotoCard
              key={item.id}
              item={item}
              idx={idx}
              arrLen={sortedItems.length}
              selectedCategory={selectedCategory}
              onOpenLightbox={onOpenLightbox}
              onMoveUp={() => move(idx, -1)}
              onMoveDown={() => move(idx, 1)}
              onRemove={() => removeItem(item.id)}
            />
          ))}

          <GhostCard onClick={() => setOpenUploadModal(true)} label="Add photo" />
        </Box>
      )}

      {/* Upload dialog */}
      <AddPhotoDialog
        open={openUploadModal}
        categoryName={selectedCategory}
        onClose={() => setOpenUploadModal(false)}
        onUploaded={handleFileUpload}
        bucketName="scenes_display"
        filenameLabel={`${selectedCategory}_${phoneNumber}`}
      />
    </Box>
  );
}
